import { useEffect, useState } from "react";
import DownloadIcon from "../../assests/images/dashboard/download.png";
import { GRAPHQL_ENDPOINT } from "src/constants/endpoints";
import { RESTAURANT_SUBSCRIPTION_TAB_DATA } from "src/constants/queries";
import { useAxiosInterceptor } from "src/hooks/useAxiosInterceptor";
import { useAuth } from "src/contexts/auth-provider";
import { FRONTEND_URL } from "src/constants/endpoints";
import SkeletonLoader from "./SkeletonLoader";
import SubcriptionLoader from "./SubcriptionLoader";
import UpcomingInvoiceSkeleton from "./UpcommingLoading";
import { formatDate } from "src/utils/date";

const SubcriptionSetting = () => {
  const [restaurantSubcription, setRestaurantSubcription] = useState<any>([]);
  const { axBe } = useAxiosInterceptor();
  const { hasRestaurant } = useAuth();
  const [loading, setloading] = useState(false);

  useEffect(() => {
    setloading(true);
    const fetchCharacters = async () => {
      try {
        const response = await axBe.post(GRAPHQL_ENDPOINT, {
          query: RESTAURANT_SUBSCRIPTION_TAB_DATA,
          variables: {
            restaurantSubscriptionTabDataInput: {
              storeCorrelationId: hasRestaurant.correlationId,
              clientBaseUrl: FRONTEND_URL,
            },
          },
        });
        const { data } = response.data;
        if (data?.restaurantSubscriptionTabData) {
          setRestaurantSubcription(data.restaurantSubscriptionTabData);
          setloading(false);
        } else {
          console.error("Unexpected result structure:", data);
          setloading(false);
        }
      } catch (error) {
        console.error("Error fetching data:", error);
        setloading(false);
      }
    };
    fetchCharacters();
  }, [axBe, hasRestaurant?.correlationId]);

  const openInvoice = (url: string) => window.open(url, "_blank");

  const cardClass = "bg-[#ffffff] rounded-[16px] px-6 py-5 shadow-[0_1px_2px_rgba(0,0,0,0.04),0_8px_24px_rgba(0,0,0,0.04)]";
  const titleClass = "text-[18px] font-semibold text-[#111827] tracking-[-0.02em]";
  const textClass = "text-[14px] font-medium text-[#4b5563]";
  const textMuted = "text-[14px] text-[#6b7280]";
  const cancellationDate =
    restaurantSubcription?.eatsbitsSubscription?.cancelAt ??
    restaurantSubcription?.eatsbitsSubscription?.currentPeriodEnd;
  const showScheduledCancellation =
    restaurantSubcription?.eatsbitsSubscription?.cancelAtPeriodEnd && cancellationDate;
  const isStripeManaged = Boolean(restaurantSubcription?.eatsbitsSubscription?.stripeSubscriptionId);
  const planTier = restaurantSubcription?.subscribedPlan?.tier?.toLowerCase();

  return (
    <div className="w-full min-w-0 max-w-[760px] space-y-3">
      {loading ? (
        <SubcriptionLoader />
      ) : (
        <>
          <div className={`${cardClass} grid grid-cols-1 md:grid-cols-2 gap-6`}>
            <div>
              <h2 className={`${titleClass} mb-4`}>Your Plan</h2>
              <p className={textMuted}>
                {restaurantSubcription?.subscribedPlan?.productName?.toLowerCase()}
              </p>
              <p className="text-[18px] font-semibold text-[#111827] mt-2">
                ${restaurantSubcription?.subscribedPlan?.priceCents}/
                {restaurantSubcription?.subscribedPlan?.frequency?.toLowerCase() === "yearly" ? "year" : "month"}
              </p>
              <p className={`${textMuted} mt-2`}>
                {restaurantSubcription?.subscribedPlan?.productDescription}
              </p>
              {showScheduledCancellation ? (
                <p className={`${textMuted} mt-2`}>
                  Active (cancels on {formatDate(cancellationDate)})
                </p>
              ) : null}
            </div>
            {isStripeManaged ? (
              <div>
                <p className={`${textClass} mt-2`}>
                  Visit your Stripe customer portal to update your payment method and view invoices.
                </p>
                <button
                  type="button"
                  className="mt-4 w-full px-4 py-2.5 rounded-xl border border-[#e5e7eb] text-[14px] font-medium text-[#FD7657] bg-white hover:bg-[#f9fafb] focus:outline-none focus:ring-2 focus:ring-[#FD7657]/20 focus:ring-offset-2"
                  onClick={() => openInvoice(restaurantSubcription?.customerPortalUrl)}
                >
                  Stripe customer portal
                </button>
              </div>
            ) : (
              <div>
                <p className={`${textClass} mt-2`}>
                  This {planTier || "enterprise"} plan is managed by EatsBits. No Stripe checkout or payment method is required.
                </p>
              </div>
            )}
          </div>

          {isStripeManaged && (loading ? (
            <UpcomingInvoiceSkeleton />
          ) : (
            <div className={cardClass}>
              <div className="flex items-center gap-4 mb-4">
                <h2 className={titleClass}>Upcoming Invoice</h2>
                <p className={textMuted}>
                  {restaurantSubcription?.upcomingInvoice?.nextPaymentAttempt != null
                    ? formatDate(restaurantSubcription.upcomingInvoice.nextPaymentAttempt * 1000)
                    : ""}
                </p>
              </div>
              <div className="flex justify-between py-2 border-b border-[#f3f4f6]">
                <p className={textClass}>{restaurantSubcription?.upcomingInvoice?.description}</p>
                <p className={textMuted}>${restaurantSubcription?.upcomingInvoice?.totalExcludingTax}</p>
              </div>
              <div className="flex justify-between py-2 mt-2">
                <p className="text-[14px] font-medium text-[#111827]">Total (excluding tax)</p>
                <p className="text-[14px] font-medium text-[#111827]">${restaurantSubcription?.upcomingInvoice?.totalExcludingTax}</p>
              </div>
            </div>
          ))}

          {isStripeManaged && <div className={cardClass}>
            <h2 className={`${titleClass} mb-4`}>Billing History</h2>
            {loading ? (
              <SkeletonLoader />
            ) : (
              <div className="overflow-x-auto overscroll-x-contain -mx-6 px-6 sm:mx-0 sm:px-0">
                <table className="w-full min-w-[560px] border-collapse text-left">
                  <thead>
                    <tr className="border-b border-[#e5e7eb]">
                      <th className="text-xs sm:text-[14px] font-medium text-[#4b5563] py-2 pr-3 sm:pr-4 whitespace-nowrap">
                        DATE
                      </th>
                      <th className="text-xs sm:text-[14px] font-medium text-[#4b5563] py-2 pr-3 sm:pr-4 min-w-[120px]">
                        DETAILS
                      </th>
                      <th className="text-xs sm:text-[14px] font-medium text-[#4b5563] py-2 pr-3 sm:pr-4 whitespace-nowrap">
                        AMOUNT
                      </th>
                      <th className="text-xs sm:text-[14px] font-medium text-[#4b5563] py-2 pr-0 whitespace-nowrap">
                        INVOICE/RECEIPT
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {restaurantSubcription?.invoices?.map((data: any, index: number) => (
                      <tr key={index} className="border-b border-[#f3f4f6]">
                        <td className="py-2 pr-3 sm:pr-4 text-[13px] sm:text-[14px] text-[#111827] whitespace-nowrap align-top">
                          {formatDate(data?.invoiceCreatedAtByStripe)}
                        </td>
                        <td className="py-2 pr-3 sm:pr-4 text-[13px] sm:text-[14px] text-[#4b5563] align-top max-w-[200px] sm:max-w-none">
                          {data?.description}
                        </td>
                        <td className="py-2 pr-3 sm:pr-4 text-[13px] sm:text-[14px] font-medium text-[#111827] whitespace-nowrap align-top">
                          ${data?.totalExcludingTax}
                        </td>
                        <td className="py-2 pr-0 align-top whitespace-nowrap">
                          <button
                            type="button"
                            className="text-[13px] font-medium text-[#FD7657] hover:underline inline-flex items-center gap-x-2"
                            onClick={() => openInvoice(data?.hostedInvoiceUrl)}
                          >
                            <img className="w-[14px] h-[14px] shrink-0" src={DownloadIcon} alt="" />
                            Download
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>}
        </>
      )}
    </div>
  );
};

export default SubcriptionSetting;
